import React, { useState } from 'react';

export interface AccordionProps {
  children: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (event: any, expanded: boolean) => void;
  className?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  children,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onChange,
  className = '',
}) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  const toggleExpanded = () => {
    const newExpanded = !expanded;
    if (isControlled) {
      onChange?.(null, newExpanded);
    } else {
      setUncontrolledExpanded(newExpanded);
    }
  };

  return (
    <div className={`border border-gray-200 rounded-lg ${className}`}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            expanded,
            onToggle: toggleExpanded,
          });
        }
        return child;
      })}
    </div>
  );
};

export interface AccordionSummaryProps {
  children: React.ReactNode;
  expandIcon?: React.ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  className?: string;
}

export const AccordionSummary: React.FC<AccordionSummaryProps> = ({
  children,
  expandIcon,
  expanded,
  onToggle,
  className = '',
}) => {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg ${className}`}
    >
      <div>{children}</div>
      {expandIcon && (
        <div
          className={`transition-transform ${expanded ? 'rotate-180' : 'rotate-0'}`}
        >
          {expandIcon}
        </div>
      )}
    </button>
  );
};

export interface AccordionDetailsProps {
  children: React.ReactNode;
  expanded?: boolean;
  className?: string;
}

export const AccordionDetails: React.FC<AccordionDetailsProps> = ({
  children,
  expanded,
  className = '',
}) => {
  if (!expanded) return null;

  return <div className={`px-4 py-3 ${className}`}>{children}</div>;
};
